import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';

import {isBadgeAction, isJourneyActionCompleted} from '@/services/loyalty/loyaltyUtils';
import {selectMemberJourneys} from '@/state/selectors/consumerLoyalty';
import {selectIsJourneyLevelTaskPending} from '@/state/selectors/loyalty';
import {setJourneyLevelTaskPending, setJourneyLevelTaskPopupState} from '@/state/slices/loyaltySlice';
import {dispatch} from '@/state/store';

const useJourneyLevelTaskComplete = () => {
    const previousJourneysRef = useRef(null);
    const isPending = useSelector(selectIsJourneyLevelTaskPending);
    const journeys = useSelector(selectMemberJourneys) || {};

    useEffect(() => {
        if (!isPending) {
            return;
        }

        if (previousJourneysRef.current === null) {
            previousJourneysRef.current = journeys;
            return;
        }

        const hasNewData = previousJourneysRef.current !== journeys;

        if (!hasNewData) {
            return;
        }

        dispatch(setJourneyLevelTaskPending(false));

        let newAction = undefined;

        for (const journey of journeys) {
            const {journeyLevels} = previousJourneysRef.current.find((j) => j.journeyId === journey.journeyId) || {};

            if (!journeyLevels) continue;
            const currentJourneyLevels = journey.journeyLevels || [];

            for (let i = 0; i < currentJourneyLevels.length; i++) {
                const currentLevel = currentJourneyLevels[i];
                const previousLevel = journeyLevels[i];

                // ICC inconsistency if previous level is missing, break the loop
                if (!previousLevel) break;

                if (currentLevel.actionsLeft < previousLevel.actionsLeft) {
                    const levelActions = currentLevel.journeyActions || [];

                    // Skip in place, never filter: getJourneyAction reads the action back by actionIndex.
                    for (let j = levelActions.length - 1; j >= 0; j--) {
                        if (isBadgeAction(levelActions[j]) || !isJourneyActionCompleted(levelActions[j])) {
                            continue;
                        }

                        newAction = {
                            actionIndex: j,
                            levelIndex: i,
                            journeyId: journey.journeyId,
                        };
                        break;
                    }
                }
            }
        }

        if (!newAction) return;

        dispatch(setJourneyLevelTaskPopupState({isVisible: true, props: {actionInfo: newAction}}));
    }, [journeys, isPending]);
};

export default useJourneyLevelTaskComplete;
